import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import ShareBar from "@/pages/detail/components/ShareBar";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getAllMovies, getMovieById, getRelatedMovies } from "@/utils/movies";

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [id]);

  usePageMeta(
    movie ? `${movie.title} - 详细介绍 - 星辰影视` : "影视作品未找到 - 星辰影视",
    movie ? movie.synopsis.slice(0, 90) : "未找到对应的影视作品信息。",
    movie ? `${movie.title},${movie.genres.join(",")},星辰影视` : "星辰影视",
  );

  if (!movie) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-20 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
          <i className="ri-film-line text-3xl" aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-heading font-bold text-xl text-foreground-950">未找到该影视作品</h1>
        <p className="mt-2 text-sm text-foreground-500">它可能已下架或链接有误</p>
        <Link
          to="/category?type=movie"
          className="mt-6 h-10 px-6 inline-flex items-center rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          返回分类页
        </Link>
      </div>
    );
  }

  const related = getRelatedMovies(getAllMovies(), movie);
  const isTv = movie.type === "tv";
  const categoryLink = `/category?type=${movie.type}&genre=${encodeURIComponent(movie.genres[0])}`;

  const metaRows = [
    { label: "上映年份", value: `${movie.year} 年` },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.castList.join(" / ") },
    { label: "类别", value: movie.genres.join(" · ") },
    { label: "地区", value: movie.region },
    { label: isTv ? "集数" : "时长", value: movie.duration },
  ];

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={movie.poster}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-30 blur-xl scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/80 via-primary-950/85 to-primary-950" />

        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10">
          <Breadcrumb
            variant="light"
            items={[{ label: isTv ? "电视剧" : "电影", to: `/category?type=${movie.type}` }, { label: movie.title }]}
          />

          <div className="mt-6 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6 md:gap-8">
            <div className="w-40 sm:w-48 md:w-full mx-auto md:mx-0 shrink-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-primary-700 bg-secondary-800">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 封面大图`}
                  title={`${movie.title} - 影视详情`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 rounded-md text-xs font-medium bg-accent-500 text-white">
                  {isTv ? "电视剧" : "电影"}
                </span>
                {movie.genres.map((genre) => (
                  <span key={genre} className="px-2.5 py-0.5 rounded-md text-xs bg-primary-700 text-primary-100">
                    {genre}
                  </span>
                ))}
              </div>

              <h1 className="mt-3 font-heading font-black text-3xl md:text-4xl text-white">
                {movie.title}
              </h1>

              <div className="mt-3 flex items-center gap-4 flex-wrap">
                <span className="flex items-center gap-1 text-accent-400 font-label font-bold text-lg">
                  <i className="ri-star-fill" aria-hidden="true" />
                  {movie.rating.toFixed(1)}
                </span>
                <span className="text-primary-200/90 text-sm font-label">{movie.year}</span>
                <span className="text-primary-200/90 text-sm">{movie.region}</span>
                <span className="text-primary-200/90 text-sm">{movie.duration}</span>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex text-sm">
                    <span className="w-20 shrink-0 text-primary-300">{row.label}</span>
                    <span className="text-white/90 min-w-0">{row.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <ShareBar title={movie.title} />
              </div>

              <div className="mt-10">
                <Link
                  to={categoryLink}
                  className="inline-flex items-center gap-1.5 text-sm text-accent-300 hover:text-accent-200 cursor-pointer"
                >
                  <i className="ri-price-tag-3-line" aria-hidden="true" />
                  所属分类：{isTv ? "电视剧" : "电影"} · {movie.genres[0]}
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-12">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
          <span className="w-1.5 h-6 rounded-full bg-accent-500" aria-hidden="true" />
          剧情简介
        </h2>
        <div className="mt-4 bg-background-50 rounded-lg border border-background-200 p-5 md:p-6">
          <p className={`text-sm md:text-base text-foreground-700 leading-relaxed ${expanded ? "" : "line-clamp-3"}`}>
            {movie.synopsis}
          </p>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-3 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
          >
            {expanded ? "收起" : "展开全文"}
            {expanded ? (
              <i className="ri-arrow-up-s-line" aria-hidden="true" />
            ) : (
              <i className="ri-arrow-down-s-line" aria-hidden="true" />
            )}
          </button>
        </div>
      </section>

      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 md:px-6 pb-14">
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
              <span className="w-1.5 h-6 rounded-full bg-accent-500" aria-hidden="true" />
              相关推荐
            </h2>
            <Link
              to={`/category?type=${movie.type}`}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              更多同类
              <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}